import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Activity, Eye, LogIn, Ban, ScrollText } from "lucide-react";
import { fmtTime } from "./format";

const ACTION_ICON = { login: LogIn, view_document: Eye, view_page: ScrollText, blocked_action: Ban, search: Activity };
const ACTIONS = ["all", "login", "logout", "view_page", "view_document", "open_document_viewer", "search", "blocked_action", "upload_document", "create", "update", "delete"];

function SummaryCards({ summary }) {
  const cards = [
    { label: "Total Events", value: summary.total, icon: Activity },
    { label: "Document Views", value: summary.document_views, icon: Eye },
    { label: "Logins", value: summary.logins, icon: LogIn },
    { label: "Blocked (DLP)", value: summary.blocked_actions, icon: Ban },
  ];
  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        {cards.map((c) => (
          <div key={c.label} className="border border-border bg-card p-5 rounded-sm">
            <div className="flex items-center justify-between">
              <span className="text-3xl font-condensed font-extrabold">{c.value}</span>
              <c.icon className="h-5 w-5 text-primary" />
            </div>
            <div className="text-xs text-muted-foreground uppercase tracking-wide mt-1">{c.label}</div>
          </div>
        ))}
      </div>
      {summary.top_users?.length > 0 && (
        <div className="border border-border bg-card rounded-sm p-4 mb-6">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Most Active Users</div>
          <div className="flex flex-wrap gap-2">
            {summary.top_users.map((u) => (
              <span key={u.email} className="text-xs bg-secondary px-2.5 py-1 rounded-sm">{u.email} · <span className="font-semibold">{u.count}</span></span>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [actionFilter, setActionFilter] = useState("all");
  useEffect(() => { api.get("/audit/summary").then((r) => setSummary(r.data)).catch((e) => console.error("audit summary", e)); }, []);
  const loadLogs = useCallback(() => {
    const params = actionFilter === "all" ? {} : { action: actionFilter };
    api.get("/audit", { params }).then((r) => setLogs(r.data)).catch((e) => console.error("audit log", e));
  }, [actionFilter]);
  useEffect(() => { loadLogs(); }, [loadLogs]);

  return (
    <>
      {summary && <SummaryCards summary={summary} />}
      <div className="flex items-center gap-3 mb-3">
        <Label className="text-sm">Filter action:</Label>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-56" data-testid="audit-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{ACTIONS.map((a) => <SelectItem key={a} value={a}>{a === "all" ? "All actions" : a}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="border border-border rounded-sm overflow-hidden" data-testid="audit-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary/60">
              <TableHead>Time</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead>
              <TableHead>Resource</TableHead><TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((l) => {
              const Icon = ACTION_ICON[l.action] || Activity;
              return (
                <TableRow key={l.id} data-testid="audit-row" className={l.action === "blocked_action" ? "bg-primary/5" : ""}>
                  <TableCell className="text-xs whitespace-nowrap text-muted-foreground">{fmtTime(l.timestamp)}</TableCell>
                  <TableCell className="text-sm">{l.user_email}</TableCell>
                  <TableCell><span className="inline-flex items-center gap-1.5 text-xs font-medium"><Icon className="h-3.5 w-3.5 text-primary" />{l.action}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{l.resource_type}</TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[280px] truncate">{l.meta ? Object.entries(l.meta).map(([k, v]) => `${k}: ${v}`).join(", ") : ""}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
